import dayjs from 'dayjs'
import type { ItemCategory, ItemNews, PostDetail, NewsData } from '~/composables/news/interface'
import { useApi } from '~/composables/common/use-api'
import { useCategory } from '~/composables/news/use-category'
import { useMetaStore } from '~/stores/meta.store'

import { listCategory, sideBarCategories } from '~/resources/news'

import { CMS_API } from '~/configs/api'
import { DATE_TIME_FORMAT } from '~/constants'

export function useNews() {
  const { request } = useApi()
  const route = useRoute()
  const router = useRouter()
  const { isMobile } = useDevice()
  const { postsPerPage, currentPage, offset } = useCategory()
  const { setMetaStore } = useMetaStore()

  const totalPost = ref(0)
  const isLoading = ref(true)
  const currentCategoryIndex = ref(0)
  
  const postDetail = reactive<PostDetail>({
    slug: '',
    title: '',
    createdDate: 0,
    categorySlug: '',
    content: '',
    publishTime: '',
    link: '',
    categories: [],
    relatedPost: []
  })

  const categorySlugCurrent = computed(() => {
    return router.currentRoute.value?.query?.cat?.toString() ?? sideBarCategories[0].slug
  })

  async function handlePage(page: number, dataNews: NewsData[], categorySlug?: string): Promise<void> {
    dataNews.splice(0)
    currentPage.value = page
    try {
      isLoading.value = true
      offset.value = postsPerPage.value * (currentPage.value - 1)
      const limit = postsPerPage.value
      await fetchClientAllCategoryData(dataNews, offset.value, limit, categorySlug)
    } catch { /* empty */ }
    isLoading.value = false
  }

  const loadMore = (dataNews: NewsData[], categorySlug = '', limitNumber = 0): void => {
    const isLoadMore = true
    offset.value = dataNews[currentCategoryIndex.value]?.data?.length || 0
    const limit = limitNumber || postsPerPage.value
    const currentCategory = findCategoryBySlug(categorySlug)
    if (!currentCategory) {
      return
    }
    void getPostsByCategory(currentCategory, dataNews, offset.value, limit, isLoadMore)
  }

  async function fetchAllCategoryData(
    dataNews: NewsData[],
    offset = 0,
    limit = 4,
    categorySlugParam: string = ''
  ): Promise<void> {
    try {
      const categorySlug = getCategorySlug(categorySlugParam)
      const currentCategory = findCategoryBySlug(categorySlug)
      if (!currentCategory) {
        router.replace('404')
        return
      }
      await fetchSSRDataForCategory(currentCategory, dataNews, offset, limit)
      isLoading.value = false
    } catch { /* empty */ }
  }

  async function fetchSSRDataForCategory(
    currentCategory: ItemCategory,
    dataNews: NewsData[],
    offset: number,
    limit: number
  ): Promise<void> {
    await useAsyncData(currentCategory.slug, async () => {
      await getPostsByCategory(currentCategory, dataNews, offset, limit)
    })
  }

  async function fetchAllCategoryDataSideBar(
    categorySlugSideBar: string,
    dataNews: NewsData[],
    offset = 0,
    limit = 4
  ): Promise<void> {
    try {
      const currentCategory = listCategory.find(item => item.slug === categorySlugSideBar)
      if (currentCategory) {
        await getPostsByCategory(currentCategory, dataNews, offset, limit)
      }
    } catch { /* empty */ }
  }

  async function fetchClientAllCategoryData(
    dataNews: NewsData[],
    offset = 0,
    limit = 4,
    categorySlugParam: string = ''
  ): Promise<void> {
    try {
      const categorySlug = getCategorySlug(categorySlugParam)
      const currentCategory = findCategoryBySlug(categorySlug)
      if (currentCategory) {
        await getPostsByCategory(currentCategory, dataNews, offset, limit)
      }
    } catch { /* empty */ }
  }

  function getCategorySlug(categorySlugParam: string): string {
    return categorySlugParam || categorySlugCurrent.value
  }

  function findCategoryBySlug(slug: string): ItemCategory | undefined {
    return listCategory.find(item => item.slug === slug)
  }

  async function getPostsByCategory(
    category: ItemCategory,
    dataNews: NewsData[],
    offset = 0,
    limit = 4,
    isLoadMore = false
  ): Promise<ItemNews[] | undefined> {
    try {
      const response = await request(CMS_API.POSTS_BY_CATEGORY, {
        method: 'GET',
        params: {
          catIds: category.subCategory,
          offset,
          limit
        }
      })
      totalPost.value = parseInt(response.total)
      const dataCategory = mapPostData(response.data)
      if (isLoadMore) {
        dataNews[currentCategoryIndex.value].data.push(...dataCategory)
        return
      }
      if (isMobile) {
        dataNews[currentCategoryIndex.value] = {
          category,
          data: dataCategory
        }
        return
      }
      dataNews.push({
        category,
        data: dataCategory
      })
      return dataCategory
    } catch { /* empty */ }
  }

  function mapPostData(data: ItemNews[]): ItemNews[] {
    return data.map((element: ItemNews) => ({
      id: element.id,
      slug: `${element.slug}.html`,
      title: element.title,
      date: formatUnitTime(element.updated_date, DATE_TIME_FORMAT.SHORT_DATE_FORMAT),
      time: formatUnitTime(element.updated_date, DATE_TIME_FORMAT.HOUR_MINUTE),
      image: element.media?.source_url ?? '',
      category: element.categories?.[0]?.name ?? '',
      desc: element.meta?.description ?? ''
    }))
  }

  async function getNewsPostData(): Promise<void> {
    try {
      const response = await request(CMS_API.POST_DETAIL, {
        method: 'GET',
        params: { slug: route.params?.slug?.toString()?.replace('.html', '') }
      })

      if (response.status === 'NO_POSTS_ERROR') {
        void router.replace('/404')
        return
      }

      const detailNews = response.data[0]
      const updateDate = detailNews.post.updated_date

      detailNews.post.publishTime = formatUnitTime(updateDate, DATE_TIME_FORMAT.PUBLISH_TIME)
      Object.assign(postDetail, detailNews.post)

      setMetaStore(response.data[0].post.meta)
    } catch { /* empty */ }
  }

  function formatUnitTime(updatedDate: number | undefined, formatTo: string): string {
    return updatedDate ? dayjs(updatedDate * 1000).format(formatTo) : ''
  }

  return {
    isLoading,
    postDetail,
    totalPost,
    getNewsPostData,
    fetchAllCategoryData,
    handlePage,
    loadMore,
    categorySlugCurrent,
    currentPage,
    postsPerPage,
    fetchAllCategoryDataSideBar,
    currentCategoryIndex
  }
}
